import React, { useRef } from "react";

/**
 * A SUBJECT, A BODY, AND THE VARIABLES THAT MAY GO IN THEM.
 *
 * The part of the Template tab that is not about A/B variants or lead pickers,
 * pulled out because three editors now need it: a call reminder's body, a
 * no-show rescue's body, and (eventually) whatever comes next. Each of those has
 * its own VARIABLE LIST, shipped from the server with the automation, which is
 * the reason this takes `variables` rather than importing a list — a reminder
 * has {{call_when}} and no slots, a rescue has slots and no join link, and an
 * editor offering a chip the renderer does not know would produce a body that
 * blocks on send.
 *
 * INSERT AT THE CURSOR, not append. A chip that always appended would be useless
 * for the one thing chips are for: dropping a variable into a sentence you are
 * already writing.
 */
export default function MessageEditor({
  subject,
  body,
  variables = [],
  onChange,
  rows = 14,
  subjectHint = null,
  label = "Email",
}) {
  const bodyRef = useRef(null);

  const insert = (name) => {
    const node = bodyRef.current;
    const token = `{{${name}}}`;
    if (!node) {
      onChange({ body: `${body}${token}` });
      return;
    }
    const start = node.selectionStart ?? body.length;
    const end = node.selectionEnd ?? start;
    const next = `${body.slice(0, start)}${token}${body.slice(end)}`;
    onChange({ body: next });
    // Put the caret after what was just inserted, on the next frame, once React
    // has written the new value.
    requestAnimationFrame(() => {
      node.focus();
      node.setSelectionRange(start + token.length, start + token.length);
    });
  };

  return (
    <div className="crm-auto-msg">
      <label className="crm-auto-subject">
        <span className="crm-label">Subject</span>
        <input
          className="crm-input"
          value={subject}
          onChange={(event) => onChange({ subject: event.target.value })}
          aria-label={`${label} subject`}
        />
      </label>
      {subjectHint && <p className="crm-hint">{subjectHint}</p>}

      <textarea
        ref={bodyRef}
        className="crm-auto-body"
        rows={rows}
        value={body}
        onChange={(event) => onChange({ body: event.target.value })}
        aria-label={`${label} body`}
      />

      <div className="crm-auto-chips">
        {variables.map((variable) => (
          <button
            key={variable.name}
            type="button"
            className="crm-auto-chip"
            title={variable.describe}
            onClick={() => insert(variable.name)}
          >
            {`{{${variable.name}}}`}
          </button>
        ))}
      </div>
    </div>
  );
}
